import { Schema } from "effect";
import {
  SandboxArtifactIdSchema,
  SandboxArtifactRevisionIdSchema,
  SandboxProjectIdSchema,
  SandboxRequestIdSchema,
  SandboxWorkspaceIdSchema,
  UploadIdSchema,
} from "./sandbox";
import {
  MEDIA_CLEANUP_BATCH_SIZE,
  MEDIA_CLEANUP_MAX_ATTEMPTS,
  MEDIA_MAX_CAPTION_BYTES,
  MEDIA_MAX_CAPTION_TRACKS,
  MEDIA_MAX_DURATION_SECONDS,
  MEDIA_MAX_SOURCE_BYTES,
  MEDIA_MULTIPART_PART_BYTES,
  MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE,
  MEDIA_NORMALIZED_MAX_FRAME_RATE,
  MEDIA_NORMALIZED_MAX_HEIGHT,
  MEDIA_NORMALIZED_MAX_WIDTH,
  MEDIA_PROCESSING_LEASE_MS,
  MEDIA_PROCESSING_DERIVATIVE_MAX_BYTES,
  MEDIA_THUMBNAIL_SPRITE_FRAME_COUNT,
  MEDIA_UPLOAD_CONCURRENCY,
  MEDIA_WAVEFORM_SAMPLE_COUNT,
} from "./media-limits";

export {
  MEDIA_CAPABILITY_TTL_SECONDS,
  MEDIA_CLEANUP_BATCH_SIZE,
  MEDIA_CLEANUP_GRACE_MS,
  MEDIA_CLEANUP_LEASE_MS,
  MEDIA_CLEANUP_MAX_ATTEMPTS,
  MEDIA_MAX_CAPTION_BYTES,
  MEDIA_MAX_CAPTION_TRACKS,
  MEDIA_MAX_DURATION_SECONDS,
  MEDIA_MAX_SOURCE_BYTES,
  MEDIA_MULTIPART_PART_BYTES,
  MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE,
  MEDIA_NORMALIZED_MAX_FRAME_RATE,
  MEDIA_NORMALIZED_MAX_HEIGHT,
  MEDIA_NORMALIZED_MAX_WIDTH,
  MEDIA_PROCESSING_HEARTBEAT_MS,
  MEDIA_PROCESSING_LEASE_MS,
  MEDIA_PROCESSING_DERIVATIVE_MAX_BYTES,
  MEDIA_THUMBNAIL_SPRITE_FRAME_COUNT,
  MEDIA_UPLOAD_CONCURRENCY,
  MEDIA_WAVEFORM_SAMPLE_COUNT,
} from "./media-limits";

export const MediaBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

const CanonicalTextSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      value === value.trim() &&
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
      }),
    { expected: "canonical text without control characters" },
  ),
);
const CanonicalIdSchema = CanonicalTextSchema.check(Schema.isMaxLength(160));
export function isCanonicalMediaTransportId(value: string): boolean {
  return /^[A-Za-z0-9_-]{1,160}$/u.test(value);
}

const MediaTransportIdSchema = CanonicalIdSchema.check(
  Schema.makeFilter(isCanonicalMediaTransportId, {
    expected: "a URL-safe media transport identifier",
  }),
);
const MediaUploadIdSchema = UploadIdSchema.check(
  Schema.makeFilter(isCanonicalMediaTransportId, {
    expected: "a URL-safe media upload identifier",
  }),
);
const IsoDateTimeSchema = Schema.String.check(
  Schema.isMaxLength(40),
  Schema.makeFilter(
    (value) =>
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/u.test(value) &&
      Number.isFinite(Date.parse(value)),
    { expected: "an ISO-8601 UTC timestamp" },
  ),
);
const PositiveIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const NonNegativeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const NonNegativeFiniteSchema = Schema.Finite.check(Schema.isGreaterThanOrEqualTo(0));
const PositiveFiniteSchema = Schema.Finite.check(Schema.isGreaterThan(0));
const Sha256Schema = Schema.String.check(
  Schema.makeFilter((value) => /^[a-f0-9]{64}$/u.test(value), {
    expected: "a lowercase SHA-256 digest",
  }),
);
const HttpUrlSchema = Schema.String.check(
  Schema.isMaxLength(8_192),
  Schema.makeFilter((value) => /^https?:\/\/[^\s/?#@]+(?:[/?#]|$)/iu.test(value), {
    expected: "an HTTP URL without credentials",
  }),
);
const MediaTypeSchema = CanonicalTextSchema.check(
  Schema.isMaxLength(160),
  Schema.makeFilter((value) => /^(?:audio|video)\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/u.test(value), {
    expected: "an audio or video media type",
  }),
);
const UploadHeaderValueSchema = Schema.String.check(
  Schema.isMaxLength(4_096),
  Schema.makeFilter(
    (value) => !value.includes("\u0000") && !value.includes("\r") && !value.includes("\n"),
    {
      expected: "a single-line upload header value",
    },
  ),
);
const UploadHeadersSchema = Schema.Record(Schema.String, UploadHeaderValueSchema).check(
  Schema.makeFilter(
    (headers) =>
      Object.keys(headers).length <= 16 &&
      Object.keys(headers).every(
        (name) =>
          /^[a-z0-9-]{1,128}$/u.test(name) &&
          !["authorization", "cookie", "proxy-authorization", "set-cookie"].includes(name),
      ),
    { expected: "at most sixteen admitted non-secret headers" },
  ),
);
export const MediaPrivateObjectKeySchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(2_048),
  Schema.makeFilter(
    (value) =>
      !value.startsWith("/") &&
      !value.includes("\\") &&
      value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== ".."),
    { expected: "a canonical private object key without traversal segments" },
  ),
);

export const MediaJobIdSchema = MediaTransportIdSchema.pipe(Schema.brand("MediaJobId"));
export const MediaCaptionIdSchema = CanonicalIdSchema.pipe(Schema.brand("MediaCaptionId"));
export const MediaCleanupJobIdSchema = CanonicalIdSchema.pipe(Schema.brand("MediaCleanupJobId"));
export const MediaObjectIdSchema = CanonicalIdSchema.pipe(Schema.brand("MediaObjectId"));
export const MediaCapabilityTokenSchema = Schema.String.check(
  Schema.makeFilter((value) => /^[A-Za-z0-9_-]{43}$/u.test(value), {
    expected: "a 256-bit URL-safe capability token",
  }),
).pipe(Schema.brand("MediaCapabilityToken"));
export const MediaKindSchema = Schema.Literals(["video", "audio"]);
export const MediaLanguageTagSchema = Schema.String.check(
  Schema.isMinLength(2),
  Schema.isMaxLength(35),
  Schema.makeFilter((value) => /^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/u.test(value), {
    expected: "a BCP 47 language tag",
  }),
);
export const MediaJobStatusSchema = Schema.Literals([
  "awaiting-upload",
  "uploaded",
  "queued",
  "probing",
  "normalizing",
  "deriving",
  "ready",
  "failed",
  "cancelled",
]);
export const MediaContainerSchema = Schema.Literals(["mp4", "m4a"]);
export const MediaVideoCodecSchema = Schema.Literal("avc1");
export const MediaAudioCodecSchema = Schema.Literal("aac");
export const MediaDerivativeRoleSchema = Schema.Literals([
  "playback",
  "poster",
  "thumbnail-sprite",
  "waveform",
  "caption",
]);
export const MediaProcessingDerivativeRoleSchema = Schema.Literals([
  "playback",
  "poster",
  "thumbnail-sprite",
  "waveform",
]);

export const MediaAdmissionSchema = Schema.Struct({
  maxSourceBytes: Schema.Literal(MEDIA_MAX_SOURCE_BYTES),
  maxDurationSeconds: Schema.Literal(MEDIA_MAX_DURATION_SECONDS),
  multipartPartBytes: Schema.Literal(MEDIA_MULTIPART_PART_BYTES),
  uploadConcurrency: Schema.Literal(MEDIA_UPLOAD_CONCURRENCY),
});

export const MediaProbeSchema = Schema.Struct({
  kind: MediaKindSchema,
  durationSeconds: PositiveFiniteSchema.check(
    Schema.isLessThanOrEqualTo(MEDIA_MAX_DURATION_SECONDS),
  ),
  width: Schema.optionalKey(PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(16_384))),
  height: Schema.optionalKey(PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(16_384))),
  rotationDegrees: Schema.Literals([0, 90, 180, 270]),
  sourceContainer: CanonicalTextSchema.check(Schema.isMaxLength(80)),
  sourceVideoCodec: Schema.optionalKey(CanonicalTextSchema.check(Schema.isMaxLength(80))),
  sourceAudioCodec: Schema.optionalKey(CanonicalTextSchema.check(Schema.isMaxLength(80))),
  hasVideo: Schema.Boolean,
  hasAudio: Schema.Boolean,
}).check(
  Schema.makeFilter(
    (probe) =>
      (probe.width === undefined) === (probe.height === undefined) &&
      (probe.kind === "video" ? probe.hasVideo : probe.hasAudio) &&
      (probe.hasVideo ||
        (probe.width === undefined &&
          probe.height === undefined &&
          probe.sourceVideoCodec === undefined)) &&
      (probe.hasAudio || probe.sourceAudioCodec === undefined),
    { expected: "track metadata consistent with the admitted media kind" },
  ),
);

export const MediaDerivativeSchema = Schema.Struct({
  id: MediaObjectIdSchema,
  role: MediaDerivativeRoleSchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
  byteSize: NonNegativeIntegerSchema,
  sha256: Sha256Schema,
  width: Schema.optionalKey(PositiveIntegerSchema),
  height: Schema.optionalKey(PositiveIntegerSchema),
  sampleCount: Schema.optionalKey(PositiveIntegerSchema),
  cueCount: Schema.optionalKey(NonNegativeIntegerSchema),
});

export const MediaCaptionTrackSchema = Schema.Struct({
  id: MediaCaptionIdSchema,
  objectId: MediaObjectIdSchema,
  label: CanonicalTextSchema.check(Schema.isMaxLength(120)),
  language: MediaLanguageTagSchema,
  kind: Schema.Literals(["captions", "subtitles"]),
  isDefault: Schema.Boolean,
  cueCount: NonNegativeIntegerSchema,
});

export const MediaManifestSchema = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  kind: MediaKindSchema,
  durationSeconds: PositiveFiniteSchema.check(
    Schema.isLessThanOrEqualTo(MEDIA_MAX_DURATION_SECONDS),
  ),
  container: MediaContainerSchema,
  videoCodec: Schema.optionalKey(MediaVideoCodecSchema),
  audioCodec: Schema.optionalKey(MediaAudioCodecSchema),
  width: Schema.optionalKey(
    PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_WIDTH)),
  ),
  height: Schema.optionalKey(
    PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_HEIGHT)),
  ),
  frameRate: Schema.optionalKey(
    PositiveFiniteSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_FRAME_RATE)),
  ),
  audioSampleRate: Schema.optionalKey(Schema.Literal(MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE)),
  playbackObjectId: MediaObjectIdSchema,
  posterObjectId: Schema.optionalKey(MediaObjectIdSchema),
  thumbnailSpriteObjectId: Schema.optionalKey(MediaObjectIdSchema),
  waveformObjectId: Schema.optionalKey(MediaObjectIdSchema),
  derivatives: Schema.Array(MediaDerivativeSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(8),
  ),
  captions: Schema.Array(MediaCaptionTrackSchema).check(
    Schema.isMaxLength(MEDIA_MAX_CAPTION_TRACKS),
  ),
}).check(
  Schema.makeFilter(
    // oxlint-disable-next-line eslint/complexity -- The canonical manifest contract checks one closed cross-field playback profile and exact derivative identity graph.
    (manifest) => {
      const expectedRoles = expectedProcessorRoles(manifest.kind);
      const byRole = (role: MediaProcessingDerivativeRole) =>
        manifest.derivatives.find((derivative) => derivative.role === role);
      const exactDerivatives =
        manifest.derivatives.length === expectedRoles.length &&
        new Set(manifest.derivatives.map((derivative) => derivative.role)).size ===
          manifest.derivatives.length &&
        expectedRoles.every((role) => {
          const derivative = byRole(role);
          return derivative?.contentType === expectedProcessorContentType(manifest.kind, role);
        });
      const objectIdsMatch =
        byRole("playback")?.id === manifest.playbackObjectId &&
        byRole("poster")?.id === manifest.posterObjectId &&
        byRole("thumbnail-sprite")?.id === manifest.thumbnailSpriteObjectId &&
        byRole("waveform")?.id === manifest.waveformObjectId;
      const audioProfileMatches =
        manifest.audioCodec === undefined
          ? manifest.audioSampleRate === undefined
          : manifest.audioCodec === "aac" &&
            manifest.audioSampleRate === MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE;
      const profileMatches =
        manifest.kind === "video"
          ? manifest.container === "mp4" &&
            manifest.videoCodec === "avc1" &&
            manifest.width !== undefined &&
            manifest.height !== undefined &&
            manifest.posterObjectId !== undefined &&
            manifest.thumbnailSpriteObjectId !== undefined
          : manifest.container === "m4a" &&
            manifest.videoCodec === undefined &&
            manifest.width === undefined &&
            manifest.height === undefined &&
            manifest.frameRate === undefined &&
            manifest.posterObjectId === undefined &&
            manifest.thumbnailSpriteObjectId === undefined &&
            manifest.audioCodec === "aac" &&
            manifest.audioSampleRate === MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE;
      return (
        exactDerivatives &&
        objectIdsMatch &&
        audioProfileMatches &&
        profileMatches &&
        manifest.captions.filter((caption) => caption.isDefault).length <= 1
      );
    },
    { expected: "the exact normalized manifest profile for the media kind" },
  ),
);

export const MediaProgressSchema = Schema.Struct({
  phase: Schema.Literals(["upload", "probe", "normalize", "derive", "finalize"]),
  completed: NonNegativeFiniteSchema,
  total: PositiveFiniteSchema,
}).check(
  Schema.makeFilter((progress) => progress.completed <= progress.total, {
    expected: "progress completed no greater than total",
  }),
);

export const MediaPublicErrorCodeSchema = Schema.Literals([
  "MEDIA_INVALID_INPUT",
  "MEDIA_TYPE_UNSUPPORTED",
  "MEDIA_SOURCE_TOO_LARGE",
  "MEDIA_DURATION_EXCEEDED",
  "MEDIA_MISSING_TRACK",
  "MEDIA_CORRUPT_SOURCE",
  "MEDIA_UPLOAD_EXPIRED",
  "MEDIA_UPLOAD_INCOMPLETE",
  "MEDIA_INTEGRITY_ERROR",
  "MEDIA_PROCESSING_FAILED",
  "MEDIA_OUTPUT_TOO_LARGE",
  "MEDIA_STORAGE_QUOTA_EXCEEDED",
  "MEDIA_RATE_LIMITED",
  "MEDIA_CANCELLED",
  "MEDIA_NOT_READY",
  "MEDIA_NOT_FOUND",
  "MEDIA_FORBIDDEN",
  "MEDIA_UNAVAILABLE",
]);
export const MediaPublicErrorSchema = Schema.Struct({
  code: MediaPublicErrorCodeSchema,
  message: CanonicalTextSchema.check(Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export const MediaWaveformSchema = Schema.Struct({
  schemaVersion: Schema.Literal(1),
  durationSeconds: PositiveFiniteSchema.check(
    Schema.isLessThanOrEqualTo(MEDIA_MAX_DURATION_SECONDS),
  ),
  peaks: Schema.Array(NonNegativeFiniteSchema.check(Schema.isLessThanOrEqualTo(1))).check(
    Schema.isMinLength(MEDIA_WAVEFORM_SAMPLE_COUNT),
    Schema.isMaxLength(MEDIA_WAVEFORM_SAMPLE_COUNT),
  ),
});
export const MediaRepositoryErrorDataSchema = Schema.Struct({
  code: Schema.Literals([
    "UNAUTHENTICATED",
    "FORBIDDEN",
    "NOT_FOUND",
    "INVALID_INPUT",
    "MEDIA_CAPTION_CONFLICT",
    "MEDIA_IDEMPOTENCY_CONFLICT",
    "MEDIA_INTEGRITY_ERROR",
    "MEDIA_JOB_CONFLICT",
    "MEDIA_JOB_STALE",
    "MEDIA_JOB_TERMINAL",
    "MEDIA_NOT_READY",
    "MEDIA_UPLOAD_CONFLICT",
    "MEDIA_UPLOAD_EXPIRED",
    "QUOTA_EXCEEDED",
    "RATE_LIMITED",
    "UPLOAD_STATE_CONFLICT",
  ]),
  message: CanonicalTextSchema.check(Schema.isMaxLength(500)),
});

export const MediaArtifactSchema = Schema.Struct({
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  jobId: MediaJobIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  kind: MediaKindSchema,
  title: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  sourceFileName: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  status: MediaJobStatusSchema,
  sourceByteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  progress: Schema.optionalKey(MediaProgressSchema),
  probe: Schema.optionalKey(MediaProbeSchema),
  manifest: Schema.optionalKey(MediaManifestSchema),
  error: Schema.optionalKey(MediaPublicErrorSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
}).check(
  Schema.makeFilter(
    (artifact) => {
      if (artifact.status === "ready") {
        return (
          (artifact.manifest !== undefined &&
            artifact.probe !== undefined &&
            artifact.error === undefined) ||
          "ready media must contain its manifest and probe without a terminal error"
        );
      }
      if (artifact.status === "failed" || artifact.status === "cancelled") {
        return (
          (artifact.error !== undefined && artifact.manifest === undefined) ||
          "failed or cancelled media must contain a terminal error without a manifest"
        );
      }
      return (
        (artifact.manifest === undefined && artifact.error === undefined) ||
        "non-terminal media must not contain a manifest or terminal error"
      );
    },
    { expected: "status-specific current media projection fields" },
  ),
);

export const CreateMediaUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  idempotencyKey: CanonicalIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  threadId: Schema.optionalKey(CanonicalIdSchema),
  kind: MediaKindSchema,
  title: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  fileName: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  contentType: MediaTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
}).check(
  Schema.makeFilter((request) => request.contentType.startsWith(`${request.kind}/`), {
    expected: "a content type matching the declared media kind",
  }),
);

export const MediaUploadSessionSchema = Schema.Struct({
  uploadId: MediaUploadIdSchema,
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  jobId: MediaJobIdSchema,
  method: Schema.Literal("MULTIPART"),
  partSizeBytes: Schema.Literal(MEDIA_MULTIPART_PART_BYTES),
  uploadConcurrency: Schema.Literal(MEDIA_UPLOAD_CONCURRENCY),
  partCount: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(10_000)),
  expiresAt: IsoDateTimeSchema,
});

export const MediaPartTicketRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  partNumbers: Schema.Array(
    Schema.Int.check(Schema.isGreaterThanOrEqualTo(1), Schema.isLessThanOrEqualTo(10_000)),
  ).check(Schema.isMinLength(1), Schema.isMaxLength(32)),
});
export const MediaPartTicketSchema = Schema.Struct({
  partNumber: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(10_000)),
  url: HttpUrlSchema,
  requiredHeaders: UploadHeadersSchema,
  expiresAt: IsoDateTimeSchema,
});
export const MediaPartTicketsSchema = Schema.NonEmptyArray(MediaPartTicketSchema).check(
  Schema.isMaxLength(32),
  Schema.makeFilter(
    (tickets) =>
      new Set(tickets.map((ticket) => ticket.partNumber)).size === tickets.length &&
      tickets.every((ticket) => ticket.expiresAt === tickets[0]?.expiresAt),
    { expected: "unique part tickets sharing one upload expiry" },
  ),
);

/** Signed Agent API -> Worker request. The provider multipart handle never leaves the Worker. */
export const BeginMediaMultipartUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  contentType: MediaTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  partCount: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(10_000)),
});
export const MediaMultipartUploadBindingSchema = Schema.Struct({
  uploadId: MediaUploadIdSchema,
  expiresAt: IsoDateTimeSchema,
});
export const IssueMediaPartTicketsRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  partNumbers: Schema.Array(
    Schema.Int.check(Schema.isGreaterThanOrEqualTo(1), Schema.isLessThanOrEqualTo(10_000)),
  ).check(Schema.isMinLength(1), Schema.isMaxLength(32)),
});

export const MediaCompletedPartSchema = Schema.Struct({
  partNumber: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(10_000)),
  etag: CanonicalTextSchema.check(Schema.isMaxLength(512)),
});
export const CompleteMediaUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  idempotencyKey: CanonicalIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  parts: Schema.Array(MediaCompletedPartSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(10_000),
  ),
});
export const CompleteMediaMultipartUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  parts: Schema.Array(MediaCompletedPartSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(10_000),
  ),
});
export const MediaCompletedObjectSchema = Schema.Struct({
  uploadId: MediaUploadIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  etag: CanonicalTextSchema.check(Schema.isMaxLength(512)),
});

export const MediaCapabilityTargetSchema = Schema.Struct({
  objectId: MediaObjectIdSchema,
  role: MediaDerivativeRoleSchema,
  objectKey: MediaPrivateObjectKeySchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
});
export const IssueMediaPlaybackCapabilitiesRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  jobId: MediaJobIdSchema,
  targets: Schema.Array(MediaCapabilityTargetSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(24),
  ),
});
export const MediaPlaybackCapabilitiesSchema = Schema.Struct({
  resources: Schema.Array(
    Schema.Struct({
      objectId: MediaObjectIdSchema,
      role: MediaDerivativeRoleSchema,
      url: HttpUrlSchema,
      contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
    }),
  ).check(Schema.isMinLength(1), Schema.isMaxLength(24)),
  expiresAt: IsoDateTimeSchema,
});

export const IssueMediaPutCapabilityRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  jobId: MediaJobIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  contentType: Schema.Literal("text/vtt"),
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_CAPTION_BYTES)),
});
export const MediaPutCapabilitySchema = Schema.Struct({
  url: HttpUrlSchema,
  completionToken: MediaCapabilityTokenSchema,
  requiredHeaders: UploadHeadersSchema,
  expiresAt: IsoDateTimeSchema,
});
export const CompleteMediaPutCapabilityRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  completionToken: MediaCapabilityTokenSchema,
});
export const MediaPutCompletedObjectSchema = Schema.Struct({
  objectKey: MediaPrivateObjectKeySchema,
  contentType: Schema.Literal("text/vtt"),
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_CAPTION_BYTES)),
  sha256: Sha256Schema,
  etag: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  cueCount: NonNegativeIntegerSchema.check(Schema.isLessThanOrEqualTo(100_000)),
});

export const CancelMediaJobRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  idempotencyKey: CanonicalIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
});
export const CancelMediaJobResponseSchema = Schema.Struct({
  status: Schema.Literals(["cancelled", "ready"]),
});

/** Signed Agent API -> Worker request issued only after Convex cancellation wins. */
export const RevokeMediaJobRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  sourceObjectKey: MediaPrivateObjectKeySchema,
  sourceContentType: MediaTypeSchema,
  sourceByteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
});
export const RevokeMediaJobResultSchema = Schema.Struct({
  status: Schema.Literal("revoked"),
});

export const MediaPlaybackTicketRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
});
export const MediaPlaybackResourceSchema = Schema.Struct({
  objectId: MediaObjectIdSchema,
  role: MediaDerivativeRoleSchema,
  url: HttpUrlSchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
});
export const MediaPlaybackTicketSchema = Schema.Struct({
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  kind: MediaKindSchema,
  durationSeconds: PositiveFiniteSchema.check(
    Schema.isLessThanOrEqualTo(MEDIA_MAX_DURATION_SECONDS),
  ),
  resources: Schema.Array(MediaPlaybackResourceSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(24),
  ),
  captions: Schema.Array(
    Schema.Struct({
      id: MediaCaptionIdSchema,
      label: CanonicalTextSchema.check(Schema.isMaxLength(120)),
      language: MediaLanguageTagSchema,
      kind: Schema.Literals(["captions", "subtitles"]),
      isDefault: Schema.Boolean,
      url: HttpUrlSchema,
    }),
  ).check(
    Schema.isMaxLength(MEDIA_MAX_CAPTION_TRACKS),
    Schema.makeFilter((captions) => captions.filter((caption) => caption.isDefault).length <= 1, {
      expected: "at most one default caption track",
    }),
  ),
  expiresAt: IsoDateTimeSchema,
});

export const AddMediaCaptionRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  idempotencyKey: CanonicalIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  label: CanonicalTextSchema.check(Schema.isMaxLength(120)),
  language: MediaLanguageTagSchema,
  kind: Schema.Literals(["captions", "subtitles"]),
  isDefault: Schema.Boolean,
  fileName: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_CAPTION_BYTES)),
});
export const CompleteMediaCaptionUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  captionId: MediaCaptionIdSchema,
  completionToken: MediaCapabilityTokenSchema,
});
export const MediaCaptionUploadCompleteSchema = Schema.Struct({
  status: Schema.Literal("ready"),
});

export const GetMediaArtifactRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  artifactId: SandboxArtifactIdSchema,
});

const MediaActorUserIdSchema = CanonicalIdSchema.pipe(Schema.brand("MediaActorUserId"));
export const ResolveMediaActorRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  operation: Schema.Literals(["read", "write"]),
});
export const ResolvedMediaActorSchema = Schema.Struct({
  userId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  role: Schema.Literals(["owner", "admin", "developer", "member", "billing", "guest"]),
});

/** Internal Agent API -> Convex repository contracts. */
export const MediaRepositoryPrepareUploadRequestSchema = Schema.Struct({
  actorUserId: MediaActorUserIdSchema,
  request: CreateMediaUploadRequestSchema,
});
export const MediaRepositoryPrepareUploadResultSchema = Schema.Struct({
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  artifactId: SandboxArtifactIdSchema,
  artifactRevisionId: SandboxArtifactRevisionIdSchema,
  sourceStorageObjectId: MediaObjectIdSchema,
  sourceObjectKey: MediaPrivateObjectKeySchema,
  derivativeTargets: Schema.Array(MediaCapabilityTargetSchema).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(4),
  ),
});
export const MediaRepositoryAttachUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  expiresAt: IsoDateTimeSchema,
});
export const MediaRepositoryMarkUploadedRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  contentType: MediaTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  sha256: Sha256Schema,
  etag: CanonicalTextSchema.check(Schema.isMaxLength(512)),
});
export const MediaRepositoryResolveUploadRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
});
export const MediaRepositoryResolvedUploadSchema = Schema.Struct({
  jobId: MediaJobIdSchema,
  uploadId: MediaUploadIdSchema,
  sourceObjectKey: MediaPrivateObjectKeySchema,
  contentType: MediaTypeSchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  status: MediaJobStatusSchema,
  expiresAt: IsoDateTimeSchema,
  sha256: Schema.optionalKey(Sha256Schema),
  etag: Schema.optionalKey(CanonicalTextSchema.check(Schema.isMaxLength(512))),
});
export const MediaRepositoryGetRequestSchema = Schema.Struct({
  actorUserId: MediaActorUserIdSchema,
  request: GetMediaArtifactRequestSchema,
});
export const MediaRepositoryResolvePlaybackRequestSchema = Schema.Struct({
  actorUserId: MediaActorUserIdSchema,
  request: MediaPlaybackTicketRequestSchema,
});
export const MediaResolvedCaptionObjectSchema = Schema.Struct({
  id: MediaCaptionIdSchema,
  objectId: MediaObjectIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  contentType: Schema.Literal("text/vtt"),
  label: CanonicalTextSchema.check(Schema.isMaxLength(120)),
  language: MediaLanguageTagSchema,
  kind: Schema.Literals(["captions", "subtitles"]),
  isDefault: Schema.Boolean,
});
export const MediaResolvedPlaybackSchema = Schema.Struct({
  jobId: MediaJobIdSchema,
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  kind: MediaKindSchema,
  durationSeconds: PositiveFiniteSchema,
  resources: Schema.Array(MediaCapabilityTargetSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(8),
  ),
  captions: Schema.Array(MediaResolvedCaptionObjectSchema).check(Schema.isMaxLength(16)),
});
export const MediaRepositoryBeginCaptionRequestSchema = Schema.Struct({
  actorUserId: MediaActorUserIdSchema,
  request: AddMediaCaptionRequestSchema,
});
export const MediaRepositoryBeginCaptionResultSchema = Schema.Struct({
  jobId: MediaJobIdSchema,
  captionId: MediaCaptionIdSchema,
  storageObjectId: MediaObjectIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  contentType: Schema.Literal("text/vtt"),
});
export const MediaRepositoryAttachCaptionRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  captionId: MediaCaptionIdSchema,
  expiresAt: IsoDateTimeSchema,
});
export const MediaRepositoryCancelCaptionRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  captionId: MediaCaptionIdSchema,
});
export const MediaRepositoryFinalizeCaptionRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  actorUserId: MediaActorUserIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  captionId: MediaCaptionIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_CAPTION_BYTES)),
  sha256: Sha256Schema,
  etag: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  cueCount: NonNegativeIntegerSchema.check(Schema.isLessThanOrEqualTo(100_000)),
});
export const MediaRepositoryCancelRequestSchema = Schema.Struct({
  actorUserId: MediaActorUserIdSchema,
  request: CancelMediaJobRequestSchema,
});
export const MediaRepositoryCancelResultSchema = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("cancelled"),
    jobId: MediaJobIdSchema,
    uploadId: MediaUploadIdSchema,
    sourceObjectKey: MediaPrivateObjectKeySchema,
    sourceContentType: MediaTypeSchema,
    sourceByteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  }),
  Schema.Struct({ status: Schema.Literal("ready") }),
]);
export const MediaCaptionUploadIntentSchema = Schema.Struct({
  captionId: MediaCaptionIdSchema,
  artifactId: SandboxArtifactIdSchema,
  revisionId: SandboxArtifactRevisionIdSchema,
  url: HttpUrlSchema,
  completionToken: MediaCapabilityTokenSchema,
  requiredHeaders: UploadHeadersSchema,
  expiresAt: IsoDateTimeSchema,
});

const MediaProcessorFilePathSchema = Schema.String.check(
  Schema.isMaxLength(4_096),
  Schema.makeFilter(
    (value) =>
      value.startsWith("/mnt/artifacts/") &&
      !value.includes("\\") &&
      value.split("/").every((segment) => segment !== "." && segment !== ".."),
    { expected: "an absolute scoped artifact mount path" },
  ),
);
const MediaProcessorTargetSchema = Schema.Struct({
  role: MediaProcessingDerivativeRoleSchema,
  path: MediaProcessorFilePathSchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
});
type MediaProcessingDerivativeRole = typeof MediaProcessingDerivativeRoleSchema.Type;

function expectedProcessorRoles(kind: MediaKind): readonly MediaProcessingDerivativeRole[] {
  return kind === "video"
    ? ["playback", "poster", "thumbnail-sprite", "waveform"]
    : ["playback", "waveform"];
}

function expectedProcessorContentType(
  kind: MediaKind,
  role: MediaProcessingDerivativeRole,
): string {
  switch (role) {
    case "playback":
      return kind === "video" ? "video/mp4" : "audio/mp4";
    case "poster":
    case "thumbnail-sprite":
      return "image/webp";
    case "waveform":
      return "application/vnd.papersflow.waveform+json";
    default:
      return role;
  }
}

export const MediaProcessorInputSchema = Schema.Struct({
  kind: MediaKindSchema,
  sourcePath: MediaProcessorFilePathSchema,
  sourceContentType: MediaTypeSchema,
  sourceByteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  targets: Schema.Array(MediaProcessorTargetSchema).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(4),
  ),
}).check(
  Schema.makeFilter(
    (input) => {
      const expectedRoles = expectedProcessorRoles(input.kind);
      return (
        input.sourceContentType.startsWith(`${input.kind}/`) &&
        input.targets.length === expectedRoles.length &&
        new Set(input.targets.map((target) => target.role)).size === input.targets.length &&
        expectedRoles.every((role) =>
          input.targets.some(
            (target) =>
              target.role === role &&
              target.contentType === expectedProcessorContentType(input.kind, role),
          ),
        )
      );
    },
    { expected: "the exact normalized derivative set for the media kind" },
  ),
);

export const MediaProcessorOutputSchema = Schema.Struct({
  role: MediaProcessingDerivativeRoleSchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  width: Schema.optionalKey(PositiveIntegerSchema),
  height: Schema.optionalKey(PositiveIntegerSchema),
  sampleCount: Schema.optionalKey(PositiveIntegerSchema),
}).check(
  Schema.makeFilter(
    (output) => output.byteSize <= MEDIA_PROCESSING_DERIVATIVE_MAX_BYTES[output.role],
    { expected: "a derivative within its role-specific byte limit" },
  ),
);
export const MediaProcessorResultSchema = Schema.Struct({
  probe: MediaProbeSchema,
  container: MediaContainerSchema,
  videoCodec: Schema.optionalKey(MediaVideoCodecSchema),
  audioCodec: Schema.optionalKey(MediaAudioCodecSchema),
  width: Schema.optionalKey(
    PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_WIDTH)),
  ),
  height: Schema.optionalKey(
    PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_HEIGHT)),
  ),
  frameRate: Schema.optionalKey(
    PositiveFiniteSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_FRAME_RATE)),
  ),
  audioSampleRate: Schema.optionalKey(Schema.Literal(MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE)),
  outputs: Schema.Array(MediaProcessorOutputSchema).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(4),
  ),
}).check(
  Schema.makeFilter(
    // oxlint-disable-next-line eslint/complexity -- The isolated processor envelope has one closed profile matrix across kind, tracks, output roles, and metadata.
    (result) => {
      const kind = result.probe.kind;
      const expectedRoles = expectedProcessorRoles(kind);
      const byRole = (role: MediaProcessingDerivativeRole) =>
        result.outputs.find((output) => output.role === role);
      const exactOutputs =
        result.outputs.length === expectedRoles.length &&
        new Set(result.outputs.map((output) => output.role)).size === result.outputs.length &&
        expectedRoles.every(
          (role) => byRole(role)?.contentType === expectedProcessorContentType(kind, role),
        );
      const playback = byRole("playback");
      const poster = byRole("poster");
      const sprite = byRole("thumbnail-sprite");
      const waveform = byRole("waveform");
      const outputMetadataMatches =
        waveform?.sampleCount === MEDIA_WAVEFORM_SAMPLE_COUNT &&
        waveform.width === undefined &&
        waveform.height === undefined &&
        playback?.sampleCount === undefined &&
        (kind === "audio"
          ? playback?.width === undefined &&
            playback?.height === undefined &&
            poster === undefined &&
            sprite === undefined
          : playback?.width === result.width &&
            playback?.height === result.height &&
            poster?.width === 1_280 &&
            poster.height === 720 &&
            poster.sampleCount === undefined &&
            sprite?.width === 160 * MEDIA_THUMBNAIL_SPRITE_FRAME_COUNT &&
            sprite.height === 90 &&
            sprite.sampleCount === MEDIA_THUMBNAIL_SPRITE_FRAME_COUNT);
      const audioProfileMatches = result.probe.hasAudio
        ? result.audioCodec === "aac" &&
          result.audioSampleRate === MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE
        : result.audioCodec === undefined && result.audioSampleRate === undefined;
      const kindProfileMatches =
        kind === "video"
          ? result.container === "mp4" &&
            result.videoCodec === "avc1" &&
            result.width !== undefined &&
            result.height !== undefined
          : result.container === "m4a" &&
            result.videoCodec === undefined &&
            result.width === undefined &&
            result.height === undefined &&
            result.frameRate === undefined;
      return exactOutputs && outputMetadataMatches && audioProfileMatches && kindProfileMatches;
    },
    { expected: "the exact verified processor result for the media kind" },
  ),
);
export const MediaProcessorFailureSchema = Schema.Struct({
  code: Schema.Literals([
    "MEDIA_TYPE_UNSUPPORTED",
    "MEDIA_DURATION_EXCEEDED",
    "MEDIA_MISSING_TRACK",
    "MEDIA_CORRUPT_SOURCE",
    "MEDIA_INTEGRITY_ERROR",
    "MEDIA_PROCESSING_FAILED",
    "MEDIA_OUTPUT_TOO_LARGE",
    "MEDIA_STORAGE_QUOTA_EXCEEDED",
  ]),
  message: CanonicalTextSchema.check(Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});
export const MediaProcessorExecutionResultSchema = Schema.Union([
  Schema.Struct({ ok: Schema.Literal(true), result: MediaProcessorResultSchema }),
  Schema.Struct({ ok: Schema.Literal(false), error: MediaProcessorFailureSchema }),
]);

export const MediaProcessingPointerSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
});
export const ListRecoverableMediaProcessingRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  before: NonNegativeIntegerSchema,
  limit: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(100)),
});
export const RecoverableMediaProcessingSchema = Schema.Struct({
  pointers: Schema.Array(MediaProcessingPointerSchema).check(Schema.isMaxLength(100)),
});
export const ClaimMediaCleanupRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  owner: CanonicalIdSchema,
  limit: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: MEDIA_CLEANUP_BATCH_SIZE })),
});
const MediaCleanupClaimFields = {
  cleanupJobId: MediaCleanupJobIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  storageObjectId: MediaObjectIdSchema,
  objectKey: MediaPrivateObjectKeySchema,
  attempt: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_CLEANUP_MAX_ATTEMPTS)),
  leaseExpiresAt: IsoDateTimeSchema,
} as const;
export const MediaCleanupClaimSchema = Schema.Union([
  Schema.Struct({
    ...MediaCleanupClaimFields,
    purpose: Schema.Literal("media-caption"),
  }),
  Schema.Struct({
    ...MediaCleanupClaimFields,
    purpose: Schema.Literal("media-source"),
    jobId: MediaJobIdSchema,
    uploadId: MediaUploadIdSchema,
    contentType: MediaTypeSchema,
    byteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  }),
  Schema.Struct({
    ...MediaCleanupClaimFields,
    purpose: Schema.Literal("media-derivative"),
  }),
]);
export const MediaCleanupClaimsSchema = Schema.Array(MediaCleanupClaimSchema).check(
  Schema.isMaxLength(MEDIA_CLEANUP_BATCH_SIZE),
);
export const MediaCleanupErrorCodeSchema = Schema.Literal("MEDIA_CLEANUP_R2_UNAVAILABLE");
export const RecordMediaCleanupResultRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  cleanupJobId: MediaCleanupJobIdSchema,
  owner: CanonicalIdSchema,
  attempt: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_CLEANUP_MAX_ATTEMPTS)),
  outcome: Schema.Literals(["confirmed", "retry"]),
  errorCode: Schema.optionalKey(MediaCleanupErrorCodeSchema),
}).check(
  Schema.makeFilter((result) => (result.outcome === "retry") === (result.errorCode !== undefined), {
    expected: "only retry cleanup results include an error code",
  }),
);
export const MediaCleanupResultSchema = Schema.Struct({
  cleanupJobId: MediaCleanupJobIdSchema,
  status: Schema.Literals(["pending", "completed", "abandoned"]),
  attempts: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_CLEANUP_MAX_ATTEMPTS)),
});
export const EnqueueMediaProcessingResultSchema = Schema.Struct({
  status: Schema.Literal("queued"),
});
export const MediaProcessingAcknowledgementSchema = Schema.Struct({
  status: Schema.Literal("accepted"),
});
export const MediaProcessingLeaseSchema = Schema.Struct({
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  leaseOwner: CanonicalIdSchema,
  leaseGeneration: PositiveIntegerSchema,
  cancellationGeneration: NonNegativeIntegerSchema,
});
export const ClaimMediaProcessingRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  workspaceId: SandboxWorkspaceIdSchema,
  projectId: SandboxProjectIdSchema,
  jobId: MediaJobIdSchema,
  leaseOwner: CanonicalIdSchema,
  leaseDurationMs: Schema.Literal(MEDIA_PROCESSING_LEASE_MS),
});
export const MediaProcessingClaimSchema = Schema.Struct({
  lease: MediaProcessingLeaseSchema,
  kind: MediaKindSchema,
  sourceObjectKey: MediaPrivateObjectKeySchema,
  sourceContentType: MediaTypeSchema,
  sourceByteSize: PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_MAX_SOURCE_BYTES)),
  derivativeTargets: Schema.Array(MediaCapabilityTargetSchema).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(4),
  ),
});
export const RecordMediaProbeRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
  probe: MediaProbeSchema,
});
export const RecordMediaProbeResultSchema = Schema.Struct({
  status: Schema.Literals(["normalizing", "failed"]),
  errorCode: Schema.optionalKey(
    Schema.Literals(["MEDIA_DURATION_EXCEEDED", "MEDIA_MISSING_TRACK"]),
  ),
});
export const RecordMediaProgressRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
  progress: MediaProgressSchema,
});
export const RenewMediaProcessingLeaseRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
});
export const RenewMediaProcessingLeaseResultSchema = Schema.Struct({
  status: Schema.Literal("active"),
  leaseExpiresAt: IsoDateTimeSchema,
});
export const ReserveMediaProcessingOutputsRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
  outputs: Schema.Array(MediaProcessorOutputSchema).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(4),
  ),
});
export const ReleaseMediaProcessingAttemptRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
});
export const MediaProcessingCommitOutputSchema = Schema.Struct({
  role: MediaProcessingDerivativeRoleSchema,
  contentType: CanonicalTextSchema.check(Schema.isMaxLength(160)),
  byteSize: PositiveIntegerSchema,
  sha256: Sha256Schema,
  etag: CanonicalTextSchema.check(Schema.isMaxLength(512)),
  width: Schema.optionalKey(PositiveIntegerSchema),
  height: Schema.optionalKey(PositiveIntegerSchema),
  sampleCount: Schema.optionalKey(PositiveIntegerSchema),
}).check(
  Schema.makeFilter(
    (output) => output.byteSize <= MEDIA_PROCESSING_DERIVATIVE_MAX_BYTES[output.role],
    { expected: "a derivative within its role-specific byte limit" },
  ),
);
export const CompleteMediaProcessingRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
  container: MediaContainerSchema,
  videoCodec: Schema.optionalKey(MediaVideoCodecSchema),
  audioCodec: Schema.optionalKey(MediaAudioCodecSchema),
  width: Schema.optionalKey(
    PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_WIDTH)),
  ),
  height: Schema.optionalKey(
    PositiveIntegerSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_HEIGHT)),
  ),
  frameRate: Schema.optionalKey(
    PositiveFiniteSchema.check(Schema.isLessThanOrEqualTo(MEDIA_NORMALIZED_MAX_FRAME_RATE)),
  ),
  audioSampleRate: Schema.optionalKey(Schema.Literal(MEDIA_NORMALIZED_AUDIO_SAMPLE_RATE)),
  outputs: Schema.Array(MediaProcessingCommitOutputSchema).check(
    Schema.isMinLength(2),
    Schema.isMaxLength(4),
  ),
});
export const FailMediaProcessingRequestSchema = Schema.Struct({
  requestId: SandboxRequestIdSchema,
  lease: MediaProcessingLeaseSchema,
  error: MediaProcessorFailureSchema,
});

export const decodeCreateMediaUploadRequest = Schema.decodeUnknownSync(
  CreateMediaUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPartTicketRequest = Schema.decodeUnknownSync(
  MediaPartTicketRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeCompleteMediaUploadRequest = Schema.decodeUnknownSync(
  CompleteMediaUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeCancelMediaJobRequest = Schema.decodeUnknownSync(
  CancelMediaJobRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeCancelMediaJobResponse = Schema.decodeUnknownSync(
  CancelMediaJobResponseSchema,
  MediaBoundaryParseOptions,
);
export const decodeRevokeMediaJobRequest = Schema.decodeUnknownSync(
  RevokeMediaJobRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeRevokeMediaJobResult = Schema.decodeUnknownSync(
  RevokeMediaJobResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPlaybackTicketRequest = Schema.decodeUnknownSync(
  MediaPlaybackTicketRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeAddMediaCaptionRequest = Schema.decodeUnknownSync(
  AddMediaCaptionRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeCompleteMediaCaptionUploadRequest = Schema.decodeUnknownSync(
  CompleteMediaCaptionUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaCaptionUploadComplete = Schema.decodeUnknownSync(
  MediaCaptionUploadCompleteSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaArtifact = Schema.decodeUnknownSync(
  MediaArtifactSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProbe = Schema.decodeUnknownSync(
  MediaProbeSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProgress = Schema.decodeUnknownSync(
  MediaProgressSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPublicError = Schema.decodeUnknownSync(
  MediaPublicErrorSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryErrorData = Schema.decodeUnknownSync(
  MediaRepositoryErrorDataSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaUploadSession = Schema.decodeUnknownSync(
  MediaUploadSessionSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPartTicket = Schema.decodeUnknownSync(
  MediaPartTicketSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPartTickets = Schema.decodeUnknownSync(
  MediaPartTicketsSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPlaybackTicket = Schema.decodeUnknownSync(
  MediaPlaybackTicketSchema,
  MediaBoundaryParseOptions,
);
export const decodeBeginMediaMultipartUploadRequest = Schema.decodeUnknownSync(
  BeginMediaMultipartUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeIssueMediaPartTicketsRequest = Schema.decodeUnknownSync(
  IssueMediaPartTicketsRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeCompleteMediaMultipartUploadRequest = Schema.decodeUnknownSync(
  CompleteMediaMultipartUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeIssueMediaPlaybackCapabilitiesRequest = Schema.decodeUnknownSync(
  IssueMediaPlaybackCapabilitiesRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeIssueMediaPutCapabilityRequest = Schema.decodeUnknownSync(
  IssueMediaPutCapabilityRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaMultipartUploadBinding = Schema.decodeUnknownSync(
  MediaMultipartUploadBindingSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaCompletedObject = Schema.decodeUnknownSync(
  MediaCompletedObjectSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPlaybackCapabilities = Schema.decodeUnknownSync(
  MediaPlaybackCapabilitiesSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPutCapability = Schema.decodeUnknownSync(
  MediaPutCapabilitySchema,
  MediaBoundaryParseOptions,
);
export const decodeCompleteMediaPutCapabilityRequest = Schema.decodeUnknownSync(
  CompleteMediaPutCapabilityRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaPutCompletedObject = Schema.decodeUnknownSync(
  MediaPutCompletedObjectSchema,
  MediaBoundaryParseOptions,
);
export const decodeGetMediaArtifactRequest = Schema.decodeUnknownSync(
  GetMediaArtifactRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeResolveMediaActorRequest = Schema.decodeUnknownSync(
  ResolveMediaActorRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeResolvedMediaActor = Schema.decodeUnknownSync(
  ResolvedMediaActorSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryPrepareUploadRequest = Schema.decodeUnknownSync(
  MediaRepositoryPrepareUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryPrepareUploadResult = Schema.decodeUnknownSync(
  MediaRepositoryPrepareUploadResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryAttachUploadRequest = Schema.decodeUnknownSync(
  MediaRepositoryAttachUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryMarkUploadedRequest = Schema.decodeUnknownSync(
  MediaRepositoryMarkUploadedRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryResolveUploadRequest = Schema.decodeUnknownSync(
  MediaRepositoryResolveUploadRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryResolvedUpload = Schema.decodeUnknownSync(
  MediaRepositoryResolvedUploadSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryGetRequest = Schema.decodeUnknownSync(
  MediaRepositoryGetRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryResolvePlaybackRequest = Schema.decodeUnknownSync(
  MediaRepositoryResolvePlaybackRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaResolvedPlayback = Schema.decodeUnknownSync(
  MediaResolvedPlaybackSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryBeginCaptionRequest = Schema.decodeUnknownSync(
  MediaRepositoryBeginCaptionRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryBeginCaptionResult = Schema.decodeUnknownSync(
  MediaRepositoryBeginCaptionResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryAttachCaptionRequest = Schema.decodeUnknownSync(
  MediaRepositoryAttachCaptionRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryCancelCaptionRequest = Schema.decodeUnknownSync(
  MediaRepositoryCancelCaptionRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryFinalizeCaptionRequest = Schema.decodeUnknownSync(
  MediaRepositoryFinalizeCaptionRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryCancelRequest = Schema.decodeUnknownSync(
  MediaRepositoryCancelRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaRepositoryCancelResult = Schema.decodeUnknownSync(
  MediaRepositoryCancelResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaCaptionUploadIntent = Schema.decodeUnknownSync(
  MediaCaptionUploadIntentSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaWaveform = Schema.decodeUnknownSync(
  MediaWaveformSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessorInput = Schema.decodeUnknownSync(
  MediaProcessorInputSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessorResult = Schema.decodeUnknownSync(
  MediaProcessorResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessorFailure = Schema.decodeUnknownSync(
  MediaProcessorFailureSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessorExecutionResult = Schema.decodeUnknownSync(
  MediaProcessorExecutionResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessingPointer = Schema.decodeUnknownSync(
  MediaProcessingPointerSchema,
  MediaBoundaryParseOptions,
);
export const decodeListRecoverableMediaProcessingRequest = Schema.decodeUnknownSync(
  ListRecoverableMediaProcessingRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeRecoverableMediaProcessing = Schema.decodeUnknownSync(
  RecoverableMediaProcessingSchema,
  MediaBoundaryParseOptions,
);
export const decodeClaimMediaCleanupRequest = Schema.decodeUnknownSync(
  ClaimMediaCleanupRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaCleanupClaims = Schema.decodeUnknownSync(
  MediaCleanupClaimsSchema,
  MediaBoundaryParseOptions,
);
export const decodeRecordMediaCleanupResultRequest = Schema.decodeUnknownSync(
  RecordMediaCleanupResultRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaCleanupResult = Schema.decodeUnknownSync(
  MediaCleanupResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeEnqueueMediaProcessingResult = Schema.decodeUnknownSync(
  EnqueueMediaProcessingResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessingAcknowledgement = Schema.decodeUnknownSync(
  MediaProcessingAcknowledgementSchema,
  MediaBoundaryParseOptions,
);
export const decodeClaimMediaProcessingRequest = Schema.decodeUnknownSync(
  ClaimMediaProcessingRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeMediaProcessingClaim = Schema.decodeUnknownSync(
  MediaProcessingClaimSchema,
  MediaBoundaryParseOptions,
);
export const decodeRecordMediaProbeRequest = Schema.decodeUnknownSync(
  RecordMediaProbeRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeRecordMediaProbeResult = Schema.decodeUnknownSync(
  RecordMediaProbeResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeRecordMediaProgressRequest = Schema.decodeUnknownSync(
  RecordMediaProgressRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeRenewMediaProcessingLeaseRequest = Schema.decodeUnknownSync(
  RenewMediaProcessingLeaseRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeRenewMediaProcessingLeaseResult = Schema.decodeUnknownSync(
  RenewMediaProcessingLeaseResultSchema,
  MediaBoundaryParseOptions,
);
export const decodeReserveMediaProcessingOutputsRequest = Schema.decodeUnknownSync(
  ReserveMediaProcessingOutputsRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeReleaseMediaProcessingAttemptRequest = Schema.decodeUnknownSync(
  ReleaseMediaProcessingAttemptRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeCompleteMediaProcessingRequest = Schema.decodeUnknownSync(
  CompleteMediaProcessingRequestSchema,
  MediaBoundaryParseOptions,
);
export const decodeFailMediaProcessingRequest = Schema.decodeUnknownSync(
  FailMediaProcessingRequestSchema,
  MediaBoundaryParseOptions,
);

export type MediaKind = typeof MediaKindSchema.Type;
export type MediaJobStatus = typeof MediaJobStatusSchema.Type;
export type MediaPublicError = typeof MediaPublicErrorSchema.Type;
export type MediaPublicErrorCode = typeof MediaPublicErrorCodeSchema.Type;
export type MediaJobId = typeof MediaJobIdSchema.Type;
export type MediaCaptionId = typeof MediaCaptionIdSchema.Type;
export type MediaObjectId = typeof MediaObjectIdSchema.Type;
export type MediaProbe = typeof MediaProbeSchema.Type;
export type MediaManifest = typeof MediaManifestSchema.Type;
export type MediaProgress = typeof MediaProgressSchema.Type;
export type MediaArtifact = typeof MediaArtifactSchema.Type;
export type MediaArtifactEncoded = typeof MediaArtifactSchema.Encoded;
export type MediaRepositoryErrorData = typeof MediaRepositoryErrorDataSchema.Type;
export type CreateMediaUploadRequest = typeof CreateMediaUploadRequestSchema.Type;
export type CreateMediaUploadRequestEncoded = typeof CreateMediaUploadRequestSchema.Encoded;
export type MediaUploadSession = typeof MediaUploadSessionSchema.Type;
export type MediaUploadSessionEncoded = typeof MediaUploadSessionSchema.Encoded;
export type MediaPartTicketRequest = typeof MediaPartTicketRequestSchema.Type;
export type MediaPartTicket = typeof MediaPartTicketSchema.Type;
export type MediaPartTickets = typeof MediaPartTicketsSchema.Type;
export type MediaCompletedPart = typeof MediaCompletedPartSchema.Type;
export type BeginMediaMultipartUploadRequest = typeof BeginMediaMultipartUploadRequestSchema.Type;
export type MediaMultipartUploadBinding = typeof MediaMultipartUploadBindingSchema.Type;
export type IssueMediaPartTicketsRequest = typeof IssueMediaPartTicketsRequestSchema.Type;
export type CompleteMediaUploadRequest = typeof CompleteMediaUploadRequestSchema.Type;
export type CompleteMediaMultipartUploadRequest =
  typeof CompleteMediaMultipartUploadRequestSchema.Type;
export type MediaCompletedObject = typeof MediaCompletedObjectSchema.Type;
export type MediaCapabilityTarget = typeof MediaCapabilityTargetSchema.Type;
export type IssueMediaPlaybackCapabilitiesRequest =
  typeof IssueMediaPlaybackCapabilitiesRequestSchema.Type;
export type MediaPlaybackCapabilities = typeof MediaPlaybackCapabilitiesSchema.Type;
export type IssueMediaPutCapabilityRequest = typeof IssueMediaPutCapabilityRequestSchema.Type;
export type MediaPutCapability = typeof MediaPutCapabilitySchema.Type;
export type CompleteMediaPutCapabilityRequest = typeof CompleteMediaPutCapabilityRequestSchema.Type;
export type MediaPutCompletedObject = typeof MediaPutCompletedObjectSchema.Type;
export type CancelMediaJobRequest = typeof CancelMediaJobRequestSchema.Type;
export type CancelMediaJobResponse = typeof CancelMediaJobResponseSchema.Type;
export type RevokeMediaJobRequest = typeof RevokeMediaJobRequestSchema.Type;
export type RevokeMediaJobResult = typeof RevokeMediaJobResultSchema.Type;
export type MediaPlaybackTicketRequest = typeof MediaPlaybackTicketRequestSchema.Type;
export type MediaPlaybackTicket = typeof MediaPlaybackTicketSchema.Type;
export type AddMediaCaptionRequest = typeof AddMediaCaptionRequestSchema.Type;
export type CompleteMediaCaptionUploadRequest = typeof CompleteMediaCaptionUploadRequestSchema.Type;
export type MediaCaptionUploadComplete = typeof MediaCaptionUploadCompleteSchema.Type;
export type GetMediaArtifactRequest = typeof GetMediaArtifactRequestSchema.Type;
export type ResolveMediaActorRequest = typeof ResolveMediaActorRequestSchema.Type;
export type ResolvedMediaActor = typeof ResolvedMediaActorSchema.Type;
export type MediaRepositoryPrepareUploadRequest =
  typeof MediaRepositoryPrepareUploadRequestSchema.Type;
export type MediaRepositoryPrepareUploadResult =
  typeof MediaRepositoryPrepareUploadResultSchema.Type;
export type MediaRepositoryAttachUploadRequest =
  typeof MediaRepositoryAttachUploadRequestSchema.Type;
export type MediaRepositoryMarkUploadedRequest =
  typeof MediaRepositoryMarkUploadedRequestSchema.Type;
export type MediaRepositoryResolveUploadRequest =
  typeof MediaRepositoryResolveUploadRequestSchema.Type;
export type MediaRepositoryResolvedUpload = typeof MediaRepositoryResolvedUploadSchema.Type;
export type MediaRepositoryGetRequest = typeof MediaRepositoryGetRequestSchema.Type;
export type MediaRepositoryResolvePlaybackRequest =
  typeof MediaRepositoryResolvePlaybackRequestSchema.Type;
export type MediaResolvedPlayback = typeof MediaResolvedPlaybackSchema.Type;
export type MediaRepositoryBeginCaptionRequest =
  typeof MediaRepositoryBeginCaptionRequestSchema.Type;
export type MediaRepositoryBeginCaptionResult = typeof MediaRepositoryBeginCaptionResultSchema.Type;
export type MediaRepositoryAttachCaptionRequest =
  typeof MediaRepositoryAttachCaptionRequestSchema.Type;
export type MediaRepositoryCancelCaptionRequest =
  typeof MediaRepositoryCancelCaptionRequestSchema.Type;
export type MediaRepositoryFinalizeCaptionRequest =
  typeof MediaRepositoryFinalizeCaptionRequestSchema.Type;
export type MediaRepositoryCancelRequest = typeof MediaRepositoryCancelRequestSchema.Type;
export type MediaRepositoryCancelResult = typeof MediaRepositoryCancelResultSchema.Type;
export type MediaCaptionUploadIntent = typeof MediaCaptionUploadIntentSchema.Type;
export type MediaWaveform = typeof MediaWaveformSchema.Type;
export type MediaProcessorInput = typeof MediaProcessorInputSchema.Type;
export type MediaProcessorOutput = typeof MediaProcessorOutputSchema.Type;
export type MediaProcessorResult = typeof MediaProcessorResultSchema.Type;
export type MediaProcessorFailure = typeof MediaProcessorFailureSchema.Type;
export type MediaProcessorExecutionResult = typeof MediaProcessorExecutionResultSchema.Type;
export type MediaProcessingPointer = typeof MediaProcessingPointerSchema.Type;
export type ListRecoverableMediaProcessingRequest =
  typeof ListRecoverableMediaProcessingRequestSchema.Type;
export type RecoverableMediaProcessing = typeof RecoverableMediaProcessingSchema.Type;
export type ClaimMediaCleanupRequest = typeof ClaimMediaCleanupRequestSchema.Type;
export type MediaCleanupClaim = typeof MediaCleanupClaimSchema.Type;
export type MediaCleanupErrorCode = typeof MediaCleanupErrorCodeSchema.Type;
export type RecordMediaCleanupResultRequest = typeof RecordMediaCleanupResultRequestSchema.Type;
export type MediaCleanupResult = typeof MediaCleanupResultSchema.Type;
export type EnqueueMediaProcessingResult = typeof EnqueueMediaProcessingResultSchema.Type;
export type MediaProcessingAcknowledgement = typeof MediaProcessingAcknowledgementSchema.Type;
export type MediaProcessingLease = typeof MediaProcessingLeaseSchema.Type;
export type ClaimMediaProcessingRequest = typeof ClaimMediaProcessingRequestSchema.Type;
export type MediaProcessingClaim = typeof MediaProcessingClaimSchema.Type;
export type RecordMediaProbeRequest = typeof RecordMediaProbeRequestSchema.Type;
export type RecordMediaProbeResult = typeof RecordMediaProbeResultSchema.Type;
export type RecordMediaProgressRequest = typeof RecordMediaProgressRequestSchema.Type;
export type RenewMediaProcessingLeaseRequest = typeof RenewMediaProcessingLeaseRequestSchema.Type;
export type RenewMediaProcessingLeaseResult = typeof RenewMediaProcessingLeaseResultSchema.Type;
export type ReserveMediaProcessingOutputsRequest =
  typeof ReserveMediaProcessingOutputsRequestSchema.Type;
export type ReleaseMediaProcessingAttemptRequest =
  typeof ReleaseMediaProcessingAttemptRequestSchema.Type;
export type MediaProcessingCommitOutput = typeof MediaProcessingCommitOutputSchema.Type;
export type CompleteMediaProcessingRequest = typeof CompleteMediaProcessingRequestSchema.Type;
export type FailMediaProcessingRequest = typeof FailMediaProcessingRequestSchema.Type;
